import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenIcon, MapIcon, BriefcaseIcon, UsersIcon, BarChartIcon, ShieldCheckIcon, QuoteIcon, CheckCircleIcon } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.5
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LandingPage() {
  return <div className="min-h-screen bg-white selection:bg-primary-100 selection:text-primary-900" data-id="element-376">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-surface-100" data-id="element-377">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 flex items-center justify-between" data-id="element-378">
          <div className="flex items-center gap-2" data-id="element-379">
            <img src="https://cdn.magicpatterns.com/uploads/fBQoASXNwH9X9DPqUswNbF/Equity_Path_Logo.png" alt="EquityPath" className="h-20 w-auto object-contain" data-id="element-380" />
          </div>
          <div className="hidden md:flex items-center gap-8" data-id="element-381">
            <a href="#features" className="text-sm font-medium text-surface-600 hover:text-surface-900 transition-colors" data-id="element-382">
              Features
            </a>
            <a href="#how-it-works" className="text-sm font-medium text-surface-600 hover:text-surface-900 transition-colors" data-id="element-383">
              How it Works
            </a>
            <a href="#employers" className="text-sm font-medium text-surface-600 hover:text-surface-900 transition-colors" data-id="element-384">
              For Employers
            </a>
          </div>
          <div className="flex items-center gap-4" data-id="element-385">
            <Link to="/dashboard" className="text-sm font-medium text-surface-600 hover:text-surface-900 transition-colors hidden sm:block" data-id="element-386">
              Sign In
            </Link>
            <Link to="/dashboard" className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm shadow-primary-600/20" data-id="element-387">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden" data-id="element-388">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.2] pointer-events-none" data-id="element-389" />
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-[800px] h-[800px] bg-primary-50 rounded-full blur-3xl opacity-50 pointer-events-none" data-id="element-390" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-391">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-392">
            <motion.div initial="initial" animate="animate" variants={staggerContainer} className="max-w-2xl" data-id="element-393">
              <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-sm font-medium mb-6 border border-primary-100" data-id="element-394">
                <span className="flex h-2 w-2 rounded-full bg-primary-600" data-id="element-395"></span>
                Empowering fair-chance hiring
              </motion.div>
              <motion.h1 variants={fadeIn} className="text-5xl lg:text-7xl font-bold text-surface-900 tracking-tight leading-[1.1] mb-6" data-id="element-396">
                Rebuild careers.
                <br data-id="element-397" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-400" data-id="element-398">
                  Redefine futures.
                </span>
              </motion.h1>
              <motion.p variants={fadeIn} className="text-lg lg:text-xl text-surface-600 mb-10 leading-relaxed" data-id="element-399">
                An enterprise-grade platform connecting driven individuals with
                future-ready skills, structured career pathways, and employers
                committed to inclusive hiring.
              </motion.p>
              <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4" data-id="element-400">
                <Link to="/dashboard" className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white px-6 py-3.5 rounded-xl text-base font-medium hover:bg-primary-700 transition-all shadow-lg shadow-primary-600/20 hover:shadow-primary-600/30" data-id="element-401">
                  Start Your Journey
                  <ArrowRightIcon className="w-5 h-5" data-id="element-402" />
                </Link>
                <a href="#employers" className="inline-flex items-center justify-center gap-2 bg-white text-surface-700 border border-surface-200 px-6 py-3.5 rounded-xl text-base font-medium hover:bg-surface-50 hover:border-surface-300 transition-all" data-id="element-403">
                  Partner as an Employer
                </a>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative lg:ml-auto" data-id="element-404">
              <div className="absolute inset-0 bg-primary-600/10 rounded-3xl transform translate-x-4 translate-y-4" data-id="element-405" />
              <img src="https://cdn.magicpatterns.com/uploads/xaMT1bg2m75FFgkCvTj5Q9/BLOG-2-2-768x550.webp" alt="Professional woman smiling" className="relative rounded-3xl shadow-2xl object-cover w-full max-w-lg mx-auto lg:mx-0 border border-surface-100" data-id="element-406" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="border-y border-surface-100 bg-surface-50/50 py-12" data-id="element-407">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-408">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12" data-id="element-409">
            {[{
            label: 'Job Placements',
            value: '2,400+'
          }, {
            label: 'Retention Rate',
            value: '94%'
          }, {
            label: 'Employer Partners',
            value: '150+'
          }, {
            label: 'Avg Starting Salary',
            value: '$52K'
          }].map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="text-center md:text-left" data-id="element-410">
                <div className="text-3xl md:text-4xl font-bold text-surface-900 mb-2 tracking-tight" data-id="element-411">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-surface-500 uppercase tracking-wide" data-id="element-412">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white" data-id="element-413">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-414">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-415">
            <h2 className="text-3xl md:text-4xl font-bold text-surface-900 mb-4 tracking-tight" data-id="element-416">
              Everything needed to succeed
            </h2>
            <p className="text-lg text-surface-600" data-id="element-417">
              A comprehensive ecosystem designed to bridge the gap between
              potential and opportunity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-418">
            {[{
            icon: BookOpenIcon,
            title: 'Learning Hub',
            desc: 'AI-driven paths in high-demand fields like Cloud Computing, Data Analytics, and AI Operations.'
          }, {
            icon: MapIcon,
            title: 'Career Pathways',
            desc: 'Clear visual roadmaps from beginner to job-ready, with milestone tracking and resume building.'
          }, {
            icon: BriefcaseIcon,
            title: 'Job Marketplace',
            desc: 'Curated board of fair-chance employers with AI-powered matching based on verified skills.'
          }, {
            icon: ShieldCheckIcon,
            title: 'Employer Portal',
            desc: 'Enterprise dashboard for companies to discover talent, track diversity impact, and hire confidently.'
          }, {
            icon: UsersIcon,
            title: 'Mentorship Layer',
            desc: 'Direct access to industry mentors, career coaches, and a supportive peer community.'
          }, {
            icon: BarChartIcon,
            title: 'Impact Analytics',
            desc: 'Robust reporting tools for governments and foundations to track placement and salary growth.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="p-8 rounded-2xl bg-surface-50 border border-surface-100 hover:border-primary-200 hover:shadow-lg hover:shadow-primary-500/5 transition-all group" data-id="element-419">
                <div className="w-12 h-12 bg-white rounded-xl border border-surface-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-420">
                  <feature.icon className="w-6 h-6 text-primary-600" data-id="element-421" />
                </div>
                <h3 className="text-xl font-semibold text-surface-900 mb-3" data-id="element-422">
                  {feature.title}
                </h3>
                <p className="text-surface-600 leading-relaxed" data-id="element-423">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-surface-900 text-white overflow-hidden" data-id="element-424">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-425">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-600/20 rounded-full blur-[100px] pointer-events-none" data-id="element-426" />

          <div className="text-center max-w-3xl mx-auto mb-16 relative z-10" data-id="element-427">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight" data-id="element-428">
              A structured path to employment
            </h2>
            <p className="text-lg text-surface-300" data-id="element-429">
              We remove the guesswork from career transitions with a proven,
              step-by-step methodology.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative z-10" data-id="element-430">
            {[{
            step: '01',
            title: 'Assess',
            desc: 'AI-driven evaluation of baseline skills and career interests.'
          }, {
            step: '02',
            title: 'Learn',
            desc: 'Complete tailored modules and earn verified industry credentials.'
          }, {
            step: '03',
            title: 'Connect',
            desc: 'Match with fair-chance employers and prepare for interviews.'
          }, {
            step: '04',
            title: 'Succeed',
            desc: 'Land the job, grow your salary, and mentor the next cohort.'
          }].map((phase, i) => <div key={i} className="relative" data-id="element-431">
                {i !== 3 && <div className="hidden md:block absolute top-8 left-1/2 w-full h-px bg-surface-700" data-id="element-432" />}
                <div className="relative z-10 bg-surface-800 w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold text-primary-400 mb-6 border border-surface-700" data-id="element-433">
                  {phase.step}
                </div>
                <h3 className="text-xl font-semibold mb-2" data-id="element-434">{phase.title}</h3>
                <p className="text-surface-400 text-sm leading-relaxed" data-id="element-435">
                  {phase.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-surface-50" data-id="element-436">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-437">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-438">
            <h2 className="text-3xl md:text-4xl font-bold text-surface-900 mb-4 tracking-tight" data-id="element-439">
              Real stories of transformation
            </h2>
            <p className="text-lg text-surface-600" data-id="element-440">
              Hear from individuals who have rebuilt their careers and the
              employers who hired them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-441">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-3xl border border-surface-200 shadow-sm relative" data-id="element-442">
              <QuoteIcon className="w-10 h-10 text-primary-100 absolute top-8 right-8" data-id="element-443" />
              <p className="text-lg text-surface-700 mb-8 relative z-10" data-id="element-444">
                "EquityPath gave me the structure and skills I needed to pivot
                into tech. The learning modules were challenging but clear, and
                the mentorship made all the difference. I'm now a Cloud Engineer
                and I've never been more optimistic about my future."
              </p>
              <div className="flex items-center gap-4" data-id="element-445">
                <img src="https://cdn.magicpatterns.com/uploads/ijCrin3oYctVRLGU6PAuJP/Screenshot_2026-04-04_at_4.34.46_PM.png" alt="Sarah Jenkins" className="w-14 h-14 rounded-full object-cover border-2 border-primary-100" data-id="element-446" />
                <div data-id="element-447">
                  <h4 className="font-semibold text-surface-900" data-id="element-448">
                    Sarah Jenkins
                  </h4>
                  <p className="text-sm text-surface-500" data-id="element-449">
                    Cloud Engineer at TechFlow
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 rounded-3xl border border-surface-200 shadow-sm relative" data-id="element-450">
              <QuoteIcon className="w-10 h-10 text-primary-100 absolute top-8 right-8" data-id="element-451" />
              <p className="text-lg text-surface-700 mb-8 relative z-10" data-id="element-452">
                "The community aspect of this platform is incredible. We push
                each other to complete certifications and prepare for
                interviews. It's not just about getting a job; it's about
                building a network of peers who understand your journey."
              </p>
              <div className="flex items-center gap-4" data-id="element-453">
                <img src="https://cdn.magicpatterns.com/uploads/83su6B2CNtVd2TmKnRZeDg/BLOG-1-768x550.webp" alt="Community Members" className="w-14 h-14 rounded-full object-cover border-2 border-primary-100 object-top" data-id="element-454" />
                <div data-id="element-455">
                  <h4 className="font-semibold text-surface-900" data-id="element-456">
                    Marcus & Team
                  </h4>
                  <p className="text-sm text-surface-500" data-id="element-457">
                    Cybersecurity Cohort '23
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Employer Section */}
      <section id="employers" className="py-24 bg-white" data-id="element-458">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-459">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-460">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-461">
              <img src="https://cdn.magicpatterns.com/uploads/b2A9siwdq1DstcraEBCfdN/BLOG-3-768x550.webp" alt="Team working in modern office" className="rounded-3xl shadow-xl object-cover w-full border border-surface-100" data-id="element-462" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="max-w-xl" data-id="element-463">
              <h2 className="text-3xl md:text-4xl font-bold text-surface-900 mb-6 tracking-tight" data-id="element-464">
                Hire verified, dedicated talent
              </h2>
              <p className="text-lg text-surface-600 mb-8 leading-relaxed" data-id="element-465">
                Join 150+ forward-thinking companies building inclusive,
                high-performing teams. Our candidates come pre-vetted with
                industry-recognized certifications and proven soft skills.
              </p>

              <ul className="space-y-4 mb-8" data-id="element-466">
                {['Access a diverse pipeline of skilled professionals', "Track your company's social impact metrics", 'Reduce turnover with highly motivated hires', 'Dedicated support from our employer success team'].map((item, i) => <li key={i} className="flex items-start gap-3 text-surface-700" data-id="element-467">
                    <div className="mt-1 bg-primary-100 rounded-full p-1" data-id="element-468">
                      <CheckCircleIcon className="w-4 h-4 text-primary-600" data-id="element-469" />
                    </div>
                    {item}
                  </li>)}
              </ul>

              <a href="#" className="inline-flex items-center justify-center gap-2 bg-surface-900 text-white px-6 py-3.5 rounded-xl text-base font-medium hover:bg-surface-800 transition-all" data-id="element-470">
                Become a Partner
                <ArrowRightIcon className="w-5 h-5" data-id="element-471" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-surface-50" data-id="element-472">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-473">
          <div className="bg-primary-900 rounded-3xl p-8 md:p-16 text-center text-white relative overflow-hidden" data-id="element-474">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-96 h-96 bg-primary-700 rounded-full blur-3xl opacity-50" data-id="element-475" />
            <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/3 w-96 h-96 bg-primary-800 rounded-full blur-3xl opacity-50" data-id="element-476" />

            <div className="relative z-10" data-id="element-477">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-478">
                Ready to build the future workforce?
              </h2>
              <p className="text-primary-100 text-lg mb-10 max-w-2xl mx-auto" data-id="element-479">
                Whether you're looking to transform your career or an employer
                seeking dedicated, skilled talent—EquityPath is your platform.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-480">
                <Link to="/dashboard" className="bg-white text-primary-900 px-8 py-4 rounded-xl text-base font-semibold hover:bg-primary-50 transition-colors shadow-lg" data-id="element-481">
                  Start Learning
                </Link>
                <Link to="/dashboard" className="bg-primary-800 text-white border border-primary-600 px-8 py-4 rounded-xl text-base font-semibold hover:bg-primary-700 transition-colors" data-id="element-482">
                  Hire Talent
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-surface-200 py-12" data-id="element-483">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-484">
          <div className="flex items-center gap-2" data-id="element-485">
            <img src="https://cdn.magicpatterns.com/uploads/fBQoASXNwH9X9DPqUswNbF/Equity_Path_Logo.png" alt="EquityPath" className="h-16 w-auto object-contain" data-id="element-486" />
          </div>
          <p className="text-surface-500 text-sm" data-id="element-487">
            © {new Date().getFullYear()} EquityPath Platform. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>;
}